import { Ref, useState } from "react";
import cx from "classnames";
import { faCheck } from "@fortawesome/pro-regular-svg-icons";

import { pluralized } from "common/utils/strings";
import { toDate } from "common/utils/dates";
import { toCount } from "common/utils/formatting";
import Icon from "common/components/Icon";
import Image from "common/components/Image";
import Spinner from "common/components/Spinner";

import { useClinicReviewsQuery } from "client/reviews/hooks";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import FetchErrorContainer from "client/common/components/errors/FetchErrorContainer";
import Pagination from "client/common/components/layouts/Pagination";
import ReviewStars from "client/reviews/components/ReviewStars";
import Counter from "client/common/components/display/Counter";
import Container from "client/common/components/layouts/Container";
import TextButton from "client/common/components/buttons/TextButton";
import Modal from "client/common/components/deprecated-modals/Modal";
import ModalLayout from "client/common/components/deprecated-modals/ModalLayout";
import Markdown from "client/common/components/content/Markdown";

import { IProductLineDetails } from "../types";

import styles from "../styles/ProductLineReviews.module.less";

const Disclaimer = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div>
      <p className={styles.disclaimer}>
        These veterinary reviews are made available on behalf of animal health providers do not
        represent the views or opinions of Vetcove or your veterinarian, nor should they be used as
        a substitute for veterinary advice regarding your pet.{" "}
        <TextButton onClick={() => setIsModalOpen(true)} icon="right">
          Learn more
        </TextButton>
      </p>
      <Modal visible={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalLayout>
          <ModalLayout.Header title="Veterinary reviews" onClose={() => setIsModalOpen(false)} />
          <ModalLayout.Content>
            <Markdown
              size="s"
              markdown="Some information on this site is provided or made available by healthcare professionals, and some content is provided by outside sources. The information and opinions contained in the following veterinarian reviews represent the views and opinions of the original creators of such content. The reviews do not necessarily represent the views or opinions of Vetcove or your veterinarian and **Vetcove makes no representations or warranties, express or implied, regarding the accuracy, applicability, fitness or completeness of the information contained therein.** The mere appearance of the veterinarian reviews on this site does not constitute an endorsement by Vetcove or your veterinarian of the views expressed therein. The reviews do not constitute the practice of veterinary medicine or pharmacy, nor do the reviews represent specific medical advice regarding your pet. Before engaging in any course of treatment, modifying an existing course of treatment, or refraining from treatment, you should consult with your veterinarian. The content here is not intended to be a substitute for veterinary advice, diagnosis or treatment; in no event should the following reviews or related content be used to make a diagnosis, determine the appropriate course of treatment, or to replace or overrule a veterinarian's judgment. **Always consult with a qualified and licensed veterinarian** and follow their advice without delay regardless of anything provided here."
            />
          </ModalLayout.Content>
        </ModalLayout>
      </Modal>
    </div>
  );
};

type ProductLineReviewsProps = {
  productLine: IProductLineDetails;
  ref?: Ref<HTMLDivElement>;
};

const ProductLineReviews = ({ productLine, ref }: ProductLineReviewsProps) => {
  const [page, setPage] = useState(1);
  const {
    data: reviews,
    isLoading,
    isRefetching,
    error,
  } = useClinicReviewsQuery({
    page,
    page_size: 10,
    sort: "",
    q: "",
    product_line: productLine.id,
  });

  const changePage = (p: number) => {
    setPage(p);

    // Scroll back to top on page change
    if (ref && "current" in ref) {
      ref.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  if (isLoading) {
    return <LoadingContainer />;
  }

  if (error || !reviews) {
    return <FetchErrorContainer entityName="clinic reviews" error={error} />;
  }

  if (!productLine.review_summary.num_clinic_reviews) {
    return null;
  }

  return (
    <Container>
      <div className={styles.main} ref={ref}>
        <div className={styles.headers}>
          <h4 className={styles.header}>Reviews from real veterinary professionals</h4>
          <div className={styles.subheader}>
            Reviews from veterinarians and veterinary staff across the United States. Always
            remember to consult with your veterinarian for medical advice.
          </div>
        </div>
        <div className={styles.summary}>
          <p className={styles.count}>
            {toCount(productLine.review_summary.num_clinic_reviews, "total review")}
            {isRefetching && <Spinner />}
          </p>
          <div className={styles.stats}>
            <div className={styles.stat}>
              <p className={styles.value}>
                <Counter value={productLine.review_summary.num_clinic_reviews} />
              </p>
              <p className={styles.description}>
                Total {pluralized(productLine.review_summary.num_clinic_reviews, "Review")}
              </p>
            </div>
            <div className={styles.stat}>
              <div className={styles.average}>
                <p className={styles.value}>
                  <Counter
                    value={productLine.review_summary.average_clinic_rating * 10}
                    formatter={(v) => (v / 10).toFixed(1)}
                  />
                </p>
                <ReviewStars rating={productLine.review_summary.average_clinic_rating} />
              </div>
              <p className={styles.description}>Average Rating</p>
            </div>
          </div>
        </div>
        <div className={styles.reviews}>
          {reviews.results.map((review) => (
            <div
              className={cx(styles.review, review.is_featured && styles.featured)}
              key={review.key}
            >
              {/* TODO: Add doctor image from backend */}
              {!!0 && (
                <div className={styles.doctor}>
                  <Image className={styles.image} src="" alt={review.author_display} />
                  <div className={styles.verified}>
                    <Icon icon={faCheck} />
                  </div>
                </div>
              )}
              <div className={styles.content}>
                <div className={styles.stars}>
                  <ReviewStars rating={review.rating} />
                </div>
                <p className={styles.title}>{review.title}</p>
                <p className={styles.body}>{review.body}</p>
                <p className={styles.info}>
                  <span>{review.author_display}</span>
                  <span>·</span>
                  <span>{toDate(review.written)}</span>
                </p>
              </div>
            </div>
          ))}
        </div>
        <Pagination paginator={reviews.paginator} onPageChange={changePage} entityName="review" />
        <Disclaimer />
      </div>
    </Container>
  );
};

type ProductLineReviewsWidgetProps = {
  productLine: IProductLineDetails;
  onClick: () => void;
};

const ProductLineReviewsWidget = ({ productLine, onClick }: ProductLineReviewsWidgetProps) => {
  if (!productLine.review_summary.num_clinic_reviews) {
    return null;
  }

  return (
    <button type="button" onClick={onClick} className={styles.widget}>
      <ReviewStars rating={productLine.review_summary.average_clinic_rating} />
      <div className={styles.count}>
        <Counter value={productLine.review_summary.num_clinic_reviews} />{" "}
        {pluralized(productLine.review_summary.num_clinic_reviews, "veterinary review")}
      </div>
    </button>
  );
};

export default Object.assign(ProductLineReviews, { Widget: ProductLineReviewsWidget });
